import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import PatrolPlanCard from '../../components/PatrolPlanCard';
import { adminApi } from '../../services/admin';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

export default function AdminSiteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: site, isLoading } = useQuery({
    queryKey: ['admin', 'sites', id],
    queryFn: () => adminApi.getSite(id!),
    enabled: !!id,
  });

  const { data: patrolPlansData } = useQuery({
    queryKey: ['admin', 'sites', id, 'patrol-plans'],
    // Server-side site filter — same paginate-past-page-1 bug guard as
    // the vendor UI.
    queryFn: () => adminApi.getPatrolPlans({ coverage__site: id! }),
    enabled: !!id,
  });
  const plans = patrolPlansData?.results ?? [];

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  if (!site) return <div className="text-center py-12"><p className="text-sm text-slate-500">Site not found</p></div>;

  return (
    <>
      <PageHeader
        title={site.name}
        description={`${site.address}, ${site.city}, ${site.state} ${site.zip_code}`}
        actions={
          <button onClick={() => navigate('/admin/sites')} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50">
            Back to Sites
          </button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Site Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Client</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.client_org_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Vendor</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.vendor_org_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge
                  label={site.vendor_assignment_status}
                  variant={site.vendor_assignment_status === 'active' ? 'success' : site.vendor_assignment_status === 'pending' ? 'warning' : 'error'}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Timezone</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.timezone}</dd>
            </div>
            {site.ingestion_email && (
              <div>
                <dt className="text-xs font-medium text-slate-400 uppercase">Ingestion Email</dt>
                <dd className="mt-1 text-sm text-slate-900 break-all">{site.ingestion_email}</dd>
              </div>
            )}
          </dl>
        </div>
        <ScoreCard label="Site Score" score={site.score} size="lg" />
      </div>

      {/* Patrol plan(s) — read-only for admin */}
      {plans.length > 0 && (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Patrol Plan{plans.length > 1 ? 's' : ''}</h2>
          <div className="space-y-4">
            {plans.map((plan) => (
              <div key={plan.id}>
                <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
                  <span>
                    {plan.vendor_acknowledged_at
                      ? `Vendor acknowledged on ${new Date(plan.vendor_acknowledged_at).toLocaleDateString()}`
                      : 'Not yet acknowledged by vendor'}
                  </span>
                </div>
                <PatrolPlanCard plan={plan} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assigned Guards */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Assigned Guards ({site.assigned_guards?.length ?? 0})
        </h2>
        {site.assigned_guards && site.assigned_guards.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {site.assigned_guards.map((g) => (
                  <tr key={g.id} className="cursor-pointer hover:bg-blue-50/50" onClick={() => navigate(`/admin/guards/${g.id}`)}>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{g.name}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`font-semibold ${getScoreColorClass(getScoreColor(g.score))}`}>{toGGScore(g.score)}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{new Date(g.assigned_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No guards assigned.</p>
        )}
      </div>
    </>
  );
}
